import type { ReactElement } from 'react';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult } from '@/shared/analytics/AnalyticsTypes';
import { sliceToPath } from './TopicSliceKey';
import { fieldCarriesConfidence, formatCount, formatPercent, humanUser, peakShareFill, share } from '@/shared/analytics/MetricFormatting';
import RankedBarList, { type RankedBarColumn } from '@/design/RankedList/RankedBarList';

interface RankedBarsVizProps {
    definition: TopicDefinition;
    result: AdHocAggregateResult;
    basePath: string;
}

type AggregateRow = AdHocAggregateResult['rows'][number];

function displayLabel(definition: TopicDefinition, value: string): string {
    return definition.primaryField === 'user_id' ? humanUser(value) : value;
}

/**
 * Ranked horizontal bars where every row is a drill-in link to the L2 evidence
 * for that segment. Each row carries its share, reach (people), and classifier
 * confidence so the breakdown is actionable at a glance. A thin adapter that
 * maps the topic aggregate onto the shared {@link RankedBarList} primitive.
 */
export default function RankedBarsViz({ definition, result, basePath }: RankedBarsVizProps): ReactElement {
    const metric = definition.headlineMetric;
    const total = result.totals[metric];
    const max = result.rows.reduce((peak, row) => Math.max(peak, row.metrics[metric]), 0);
    const showConfidence = fieldCarriesConfidence(definition.primaryField);

    const keyOf = (row: AggregateRow): string => row.keys[definition.primaryField] ?? '—';

    const columns: RankedBarColumn<AggregateRow>[] = [
        { key: 'users', width: 54, render: (row) => formatCount(row.metrics.users), sublabel: () => 'people' },
    ];
    if (showConfidence) {
        columns.push({ key: 'confidence', width: 54, render: (row) => formatPercent(row.metrics.avgConfidence), sublabel: () => 'conf.' });
    }

    return (
        <RankedBarList
            rows={result.rows}
            rowKey={keyOf}
            hrefFor={(row) => sliceToPath(basePath, definition.id, { primary: row.keys[definition.primaryField] ?? '' })}
            accent={definition.accent}
            label={(row) => displayLabel(definition, keyOf(row))}
            fill={(row) => peakShareFill(row.metrics[metric], max)}
            primaryStat={(row) => formatCount(row.metrics[metric])}
            share={(row) => formatPercent(share(row.metrics[metric], total))}
            columns={columns}
            pageSize={definition.rankPageSize}
            unitPlural={definition.lensPlural}
        />
    );
}
